/**
 * API SERVICE — All backend communication.
 * Mocks are clearly marked. To go live: remove mock block, uncomment real call.
 */

import { CONFIG } from './config.js';
import { delay }  from './utils.js';

// ─── Auth token helper ────────────────────────────────────────────────────────

function authHeader() {
  const token = localStorage.getItem('SAFRAN_AUTH_TOKEN');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function storeAuthToken(token) {
  if (token) localStorage.setItem('SAFRAN_AUTH_TOKEN', token);
}

export function clearAuthToken() {
  localStorage.removeItem('SAFRAN_AUTH_TOKEN');
}

// ─── Generic fetch wrapper ────────────────────────────────────────────────────

async function request(path, options = {}) {
  const url = `${CONFIG.API_BASE_URL}${path}`;
  const res  = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (!res.ok) {
    let message = `خطای سرور: ${res.status}`;
    try { const b = await res.json(); message = b.message || b.error || message; } catch (_) {}
    throw new Error(message);
  }

  return res.json();
}

// ─── WebSocket / SSE for real-time restaurant status ─────────────────────────

/**
 * Subscribe to real-time restaurant-state pushes (isOpen, product availability).
 * Backend should emit JSON: { type: 'STATUS', isOpen: bool }
 *                       or: { type: 'PRODUCT', id: string, available: bool }
 *
 * Returns a cleanup function — call it to disconnect.
 *
 * Real implementation: replace EventSource with WebSocket if your backend uses WS.
 */
export function subscribeToRestaurantStatus(onUpdate) {
  // ── MOCK — no-op, returns immediate cleanup ──────────────────────────────
  return () => {};
  // ── END MOCK ─────────────────────────────────────────────────────────────

  // Real SSE implementation:
  // const es = new EventSource(`${CONFIG.API_BASE_URL}/status/stream`);
  // es.onmessage = e => { try { onUpdate(JSON.parse(e.data)); } catch (_) {} };
  // es.onerror   = () => es.close();
  // return () => es.close();

  // Real WebSocket implementation:
  // const ws = new WebSocket(`wss://your-api.com/status`);
  // ws.onmessage = e => { try { onUpdate(JSON.parse(e.data)); } catch (_) {} };
  // return () => ws.close();
}

// ─── Menu ─────────────────────────────────────────────────────────────────────

/**
 * GET /api/menu
 * Response: {
 *   restaurantConfig: { isOpen, openTime, defaultDeliveryFee, deliveryFeeThreshold },
 *   categories: [{ id, name }],
 *   products:   [{ id, name, description, price, category, available, badge, image,
 *                  afzoodanis, gheymat_afzoodanis }],
 *   coupons:    [{ code, type, value, minOrder }]   // optional — backend may omit
 * }
 */
export async function fetchMenu() {
  return request('/menu');
}

// ─── Coupon ───────────────────────────────────────────────────────────────────

/**
 * POST /api/coupons/validate  { code }
 * Response: { coupon: { type, value, minOrder } } | { coupon: null }
 */
export async function validateCoupon(code) {
  // ── MOCK ──────────────────────────────────────────────────────────────────
  const MOCK = {
    'SAFRAN10':  { type: 'percent', value: 10,    minOrder: 150000 },
    'WELCOME50': { type: 'flat',    value: 50000, minOrder: 200000 },
  };
  await delay(400);
  return MOCK[code] || null;
  // ── END MOCK ──────────────────────────────────────────────────────────────

  // const data = await request('/coupons/validate', {
  //   method: 'POST',
  //   body: JSON.stringify({ code }),
  // });
  // return data.coupon || null;
}

// ─── OTP ──────────────────────────────────────────────────────────────────────

/**
 * POST /api/auth/send-otp  { phone }
 * Backend generates OTP, calls SMS provider (Kavenegar / Ghasedak), stores hash in DB.
 *
 * Kavenegar (server-side):
 *   POST https://api.kavenegar.com/v1/{KEY}/verify/lookup.json
 *   { receptor: phone, token: otp, template: 'verify' }
 *
 * Ghasedak (server-side):
 *   POST https://api.ghasedak.me/v2/verification/send/simple
 *   Headers: { apikey: KEY }
 *   Body: { receptor: phone, type: 1, template: 'verify', param1: otp }
 */
export async function sendOTP(phone) {
  if (!/^09\d{9}$/.test(phone)) {
    throw new Error('فرمت شماره موبایل نامعتبر است. فرمت صحیح: 09XXXXXXXXX');
  }
  // ── MOCK ──────────────────────────────────────────────────────────────────
  await delay(800);
  return { success: true };
  // ── END MOCK ──────────────────────────────────────────────────────────────

  // return request('/auth/send-otp', { method: 'POST', body: JSON.stringify({ phone }) });
}

/**
 * POST /api/auth/verify-otp  { phone, code }
 * Response: { success: true, token: 'jwt...' }
 */
export async function verifyOTP(phone, code) {
  // ── MOCK ──────────────────────────────────────────────────────────────────
  await delay(800);
  if (code === '1234') return { success: true, token: 'jwt_mock_token' };
  throw new Error('کد تایید نامعتبر است. (برای ورود آزمایشی از ۱۲۳۴ استفاده کنید)');
  // ── END MOCK ──────────────────────────────────────────────────────────────

  // return request('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ phone, code }) });
}

// ─── Order ────────────────────────────────────────────────────────────────────

/**
 * POST /api/orders  { ...fullOrderPayload }
 *
 * Full payload shape:
 * {
 *   clientPhone, receiver: { name, phone },
 *   orderType, address, location: { lat, lng },
 *   items: [{ productId, quantity, selectedAddons, linePrice }],
 *   orderNotes, couponCode,
 *   financials: { subtotal, discount, deliveryFee, grandTotal }
 * }
 *
 * Response (no gateway):  { success, orderId, estimatedMinutes }
 * Response (with gateway): { success, orderId, paymentUrl }
 *
 * Iranian gateway flow:
 *   ZarinPal sandbox: https://sandbox.zarinpal.com/pg/StartPay/{authority}
 *   IDPay:            https://idpay.ir/p/ws/{id}
 *   NextPay:          https://nextpay.org/nx/gateway/payment/{transId}
 */
export async function submitOrder(payload) {
  // ── MOCK ──────────────────────────────────────────────────────────────────
  await delay(1500);
  return {
    success:          true,
    orderId:          'SAFRAN-' + Math.floor(100000 + Math.random() * 900000),
    estimatedMinutes: 45,
    // paymentUrl: 'https://sandbox.zarinpal.com/pg/StartPay/MOCK_AUTHORITY',
  };
  // ── END MOCK ──────────────────────────────────────────────────────────────

  // return request('/orders', {
  //   method:  'POST',
  //   headers: authHeader(),
  //   body:    JSON.stringify(payload),
  // });
}

// ─── Payment Verification ─────────────────────────────────────────────────────

/**
 * POST /api/payment/verify  { orderId, authority }
 * Server calls ZarinPal/IDPay verify endpoint → confirms order in DB.
 * NEVER trust client-side gateway params alone — always verify server-side.
 *
 * Response: { success, orderId, refId, estimatedMinutes }
 * On failure backend should return non-2xx with { message }
 */
export async function verifyPayment({ orderId, authority }) {
  // ── MOCK ──────────────────────────────────────────────────────────────────
  await delay(800);
  return { success: true, orderId, refId: 'REF-' + Math.floor(Math.random() * 1e8), estimatedMinutes: 45 };
  // ── END MOCK ──────────────────────────────────────────────────────────────

  // return request('/payment/verify', {
  //   method:  'POST',
  //   headers: authHeader(),
  //   body:    JSON.stringify({ orderId, authority }),
  // });
}
